/**
 * OpenItems - Shows open/unpaid items for the customer
 * Displays unpaid invoices and unbilled appointments
 * Optimized with API caching
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertCircle, Receipt, Calendar, CreditCard, 
  ExternalLink, ChevronDown, ChevronUp, Clock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCustomerAuth, API } from './PortalContext';
import { useCachedFetch, CacheTTL } from '@/utils/useCachedFetch';
import { formatCHF } from '@/utils/format';
import PaymentSuccessDialog from './PaymentSuccessDialog';
import { watchInvoicePayment } from '@/utils/invoicePaymentWatch';

const OpenItems = ({ onPayInvoice }) => {
  const { token } = useCustomerAuth();
  const [expanded, setExpanded] = useState(false); // standardmässig eingeklappt (Aufräumen 07/2026)
  const [success, setSuccess] = useState(null);
  const watchersRef = useRef([]);

  useEffect(() => {
    return () => { watchersRef.current.forEach((c) => c && c()); };
  }, []);

  // Cached open items fetch
  const fetchOpenItems = useCallback(async () => {
    const response = await axios.get(`${API}/portal/open-items`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }, [token]);

  const { 
    data: openItems, 
    loading, 
    refresh: refreshOpenItems 
  } = useCachedFetch(
    `open-items-${token?.slice(-8)}`,
    fetchOpenItems,
    { ttl: CacheTTL.SHORT, enabled: !!token }
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `CHF ${formatCHF(amount)}`;
  };

  const startPaymentWatch = (invoiceId) => {
    const cleanup = watchInvoicePayment({
      token,
      invoiceId,
      onPaid: (paid) => {
        setSuccess({
          invoice_number: paid.invoice_number,
          total: paid.total,
          studio_name: paid.studio_name,
          method: paid.payment_method_display || 'Karte / TWINT',
        });
        refreshOpenItems();
      },
    });
    watchersRef.current.push(cleanup);
  };

  const handlePayInvoice = async (invoice) => {
    if (invoice.payment_link) {
      window.open(invoice.payment_link, '_blank');
      startPaymentWatch(invoice.id);
    } else if (onPayInvoice) {
      onPayInvoice(invoice);
      startPaymentWatch(invoice.id);
    } else {
      // Try to get payment link
      try {
        const response = await axios.post(
          `${API}/portal/invoices/${invoice.id}/pay`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.payment_link) {
          window.open(response.data.payment_link, '_blank');
          startPaymentWatch(invoice.id);
        } else if (response.data.can_pay_with_credit) {
          toast.info('Diese Rechnung kannst du im Tab „Belege" mit deinem Guthaben bezahlen.');
        }
      } catch (error) {
        toast.error('Zahlung konnte nicht initiiert werden');
      }
    }
  };

  if (loading) {
    return (
      <Card className="bg-white/5">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="animate-spin h-6 w-6 border-2 border-yellow-500 border-t-transparent rounded-full" />
        </CardContent>
      </Card>
    );
  }

  const hasOpenItems = openItems?.summary?.total_open_amount > 0 || 
                       openItems?.summary?.open_invoices_count > 0 || 
                       openItems?.summary?.unbilled_appointments_count > 0;

  if (!hasOpenItems) {
    // Keine offenen Positionen mehr — aber Erfolgs-Bestätigung ggf. noch zeigen
    return (
      <PaymentSuccessDialog
        open={!!success}
        onClose={() => setSuccess(null)}
        invoiceNumber={success?.invoice_number}
        amount={success?.total}
        studioName={success?.studio_name}
        method={success?.method}
      />
    );
  }

  return (
    <>
    <Card className="bg-white/5 border-orange-200">
      <CardHeader 
        className="cursor-pointer hover:bg-white/5 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <AlertCircle className="h-4 w-4 text-orange-600" />
            </div>
            <div>
              <CardTitle className="text-sm text-orange-800">Offene Positionen</CardTitle>
              <CardDescription className="text-xs text-orange-600">
                {formatCurrency(openItems?.summary?.total_open_amount)} offen
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {openItems?.summary?.open_invoices_count > 0 && (
              <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-700 border-orange-300 px-1.5 py-0.5">
                {openItems.summary.open_invoices_count} Rechnung(en)
              </Badge>
            )}
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-white/60" />
            ) : (
              <ChevronDown className="h-4 w-4 text-white/60" />
            )}
          </div>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent className="space-y-3 pt-2">
          {/* Unpaid Invoices */}
          {openItems?.open_items?.invoices?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-white/90 flex items-center gap-1.5">
                <Receipt className="h-3 w-3" />
                Offene Rechnungen
              </h4>
              {openItems.open_items.invoices.map((invoice) => (
                <div 
                  key={invoice.id}
                  className="p-2.5 bg-orange-50 border border-orange-200 rounded-lg"
                  data-testid={`open-invoice-${invoice.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{invoice.description}</p>
                      <div className="flex items-center gap-2 text-xs text-white/80 mt-0.5">
                        <span className="flex items-center gap-0.5">
                          <Calendar className="h-2.5 w-2.5" />
                          {formatDate(invoice.date)}
                        </span>
                        {invoice.due_date && (
                          <span className="flex items-center gap-0.5">
                            <Clock className="h-2.5 w-2.5" />
                            Fällig: {formatDate(invoice.due_date)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <p className="text-sm font-bold text-orange-700">{formatCurrency(invoice.amount)}</p>
                        <Badge 
                          variant="outline" 
                          className={`text-[10px] px-1.5 py-0 ${
                            invoice.status === 'overdue' 
                              ? 'bg-red-50 text-red-700 border-red-300' 
                              : 'bg-orange-50 text-orange-700 border-orange-300'
                          }`}
                        >
                          {invoice.status_display}
                        </Badge>
                      </div>
                      <Button
                        size="sm"
                        className="text-xs h-7 px-2 bg-orange-600 hover:bg-orange-700 text-white"
                        onClick={() => handlePayInvoice(invoice)}
                        data-testid={`pay-invoice-${invoice.id}`}
                      >
                        <CreditCard className="h-3 w-3 mr-1" />
                        Bezahlen
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Unbilled Appointments */}
          {openItems?.open_items?.appointments?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-white/90 flex items-center gap-1.5">
                <Calendar className="h-3 w-3" />
                Noch nicht abgerechnete Termine
              </h4>
              <div className="text-[10px] text-white/70 mb-1.5">
                Diese Termine wurden absolviert, aber noch nicht abgerechnet
              </div>
              {openItems.open_items.appointments.map((apt) => (
                <div 
                  key={apt.id}
                  className="p-2 bg-white/5 border border-white/10 rounded-lg"
                  data-testid={`unbilled-apt-${apt.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{apt.description}</p>
                      <p className="text-xs text-white/70">{formatDate(apt.date)}</p>
                    </div>
                    <div className="text-right">
                      {apt.amount > 0 && (
                        <p className="text-sm font-semibold text-white/90">{formatCurrency(apt.amount)}</p>
                      )}
                      {apt.points_cost > 0 && (
                        <p className="text-[10px] text-white/70">{apt.points_cost} Punkte</p>
                      )}
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 mt-0.5">
                        {apt.status_display}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary */}
          <div className="pt-2 border-t border-white/10">
            <div className="flex items-center justify-between text-xs">
              <span className="text-white/80">Gesamtbetrag offen:</span>
              <span className="font-bold text-orange-700 text-sm">
                {formatCurrency(openItems?.summary?.total_open_amount)}
              </span>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
    <PaymentSuccessDialog
      open={!!success}
      onClose={() => setSuccess(null)}
      invoiceNumber={success?.invoice_number}
      amount={success?.total}
      studioName={success?.studio_name}
      method={success?.method}
    />
    </>
  );
};

export default OpenItems;
